import * as z from "zod";
import { userFacingError } from "../authorization";
import { grantsForScope } from "../rbac/grantQueries";
import { fetchCustomRole } from "../rbac/grantResources";
import type { BuiltinRoleId } from "../rbac/roles";
import {
  assertDependentPrincipalsMeetRequirements,
  assertValidSpaceDefaultJoinRole,
} from "../rbac/validation";
import { mutator } from "./mutator";
import { generateId } from "../id";

const joinSpaceSchema = z.object({
  spaceId: z.string(),
});

export const joinSpace = mutator({
  schema: joinSpaceSchema,
  permissions: {
    none: "self-service joining, gated in fn by the space’s default join role",
  },
  resolve: {
    space: { kind: "space", from: "spaceId" },
  },
  fn: async ({ tx, ctx, args: { space } }) => {
    if (space.mode === "personal") {
      throw userFacingError("This space cannot be joined");
    }

    const builtinRoleId = space.defaultJoinBuiltinRoleId;
    const customRoleId = space.defaultJoinCustomRoleId;
    if (!builtinRoleId && !customRoleId) {
      throw userFacingError("This space is not discoverable");
    }

    const customRole = customRoleId
      ? await fetchCustomRole(tx, customRoleId, ctx.tenantId)
      : null;
    assertValidSpaceDefaultJoinRole({
      builtinRoleId: builtinRoleId as BuiltinRoleId | null,
      customRole,
    });

    const existingGrant = await tx.run(
      grantsForScope(space.id, ctx.tenantId).where("userId", ctx.userId)
    );
    if (existingGrant.length > 0) return;

    const principalGrant = {
      userId: ctx.userId,
      userGroupId: null,
      builtinRoleId: builtinRoleId ?? null,
      customRole: customRole
        ? { rolePermissions: customRole.rolePermissions }
        : null,
    };
    for (const ruleId of [
      "space_uses_connected_apps",
      "space_uses_skills",
    ] as const) {
      await assertDependentPrincipalsMeetRequirements(
        tx,
        ctx.tenantId,
        ruleId,
        space.id,
        [principalGrant]
      );
    }

    await tx.mutate.roleGrants.insert({
      id: generateId(),
      userId: ctx.userId,
      builtinRoleId: (builtinRoleId as BuiltinRoleId | null) ?? undefined,
      customRoleId: customRoleId ?? undefined,
      scopeId: space.id,
      createdAt: Date.now(),
      tenantId: ctx.tenantId,
    });
  },
});
